<template>
    <div>
        <b-row>
            <b-col>
                <StripeVueCard :patient="patient" @cardAdded="newCardAdded" />
            </b-col>
        </b-row>
        <b-row v-if="filteredCards.length">
            <b-col>
                <el-table :data="filteredCards" class="table-shopping border">
                    <el-table-column label="Card" prop="brand" />
                    <el-table-column label="Number" prop="cardNumber" />
                    <el-table-column label="Expire">
                        <template slot-scope="props">
                            {{ props.row.expMonth }}/{{ props.row.expYear }}
                        </template>
                    </el-table-column>
                    <el-table-column v-if="filteredCards.find(fc => fc.hasActiveSubscription)"
                        label="On Active Subscription" prop="hasActiveSubscription">
                        <template slot-scope="props">
                            {{ props.row.hasActiveSubscription ? "✔" : "" }}
                        </template>
                    </el-table-column>
                    <el-table-column min-width="50" header-align="right" label="">
                        <div slot-scope="{row}" class="text-right">
                            <a href="#" @click.prevent="deleteConfirmDialog(row)">
                                <b-icon icon="x-circle-fill" class="mr-1" variant="danger" v-b-tooltip.hover
                                    title="Remove" />
                            </a>
                        </div>
                    </el-table-column>
                </el-table>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Patient } from '@/models/Patient';
import axios, { AxiosRequestConfig } from 'axios';
import { Table, TableColumn } from 'element-ui'
import StripeVueCard from '@/components/Payments/StripeVueCardcomponent.vue';
import { StripeCard } from '@/models/Payment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "PatientPaymentMethodsModal",
    components: {
        StripeVueCard,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
    }
})

export default class PatientPaymentMethodsModal extends Vue {
    @Prop() patient!: Patient;

    private stripeCustomerId: string | null = this.patient?.stripeCustomerId;
    private stripeCardList: Array<StripeCard> = [];

    created() {
        this.refresh();
    }

    @Watch('patient.stripeCustomerId')
    refresh() {
        this.stripeCustomerId = this.patient?.stripeCustomerId;
        this.fetchPatientPaymentMethods();
    }

    newCardAdded(stripeResponse: StripeCard) {
        this.stripeCustomerId = stripeResponse.customerId;
        this.fetchPatientPaymentMethods();
    }

    async fetchPatientPaymentMethods(): Promise<number | any> {
        try {
            this.stripeCardList = [];
            if (!this.stripeCustomerId) return;
            const response = await axios.get(`/stripe/cards/${this.stripeCustomerId}`);
            this.stripeCardList = response.data;
        } catch (error) {
            console.error("Error while getting Payment Cards from Patient", error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async deleteCard(card: StripeCard): Promise<number | any> {
        try {
            const config: AxiosRequestConfig = {
                params: {
                    patientid: this.patient.id,
                    customerid: card.customerId
                }
            };
            await axios.delete(`/stripe/${card.id}`, config);
        } catch (error) {
            console.error("Error deleting Payment Card from Patient", error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.fetchPatientPaymentMethods();
        }
    }

    async deleteConfirmDialog(card: StripeCard) {
        try {
            const msg = "Are you sure you want to delete this Payment Method?";
            const result = await this.$bvModal.msgBoxConfirm(msg, {
                title: 'Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });

            if (!result) return;
            await this.deleteCard(card);
        } catch (err) {
            console.error("Error caught on Delete button.", err);
        }
    }

    get filteredCards() {
        return this.stripeCardList?.filter(p => !p.pendingDelete);
    }
}
</script>
